import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Modal from "../../components/Modal";
import Alert from "../../components/Alert";
import {
  listPlatformInstitutes,
  createPlatformInstitute,
  activatePlatformInstitute,
  deactivatePlatformInstitute,
} from "../../api/platformInstituteApi";
import "../Users/UsersPage.css";

const emptyForm = {
  instituteName: "",
  address: "",
  instituteEmail: "",
  instituteContactNumber: "",
  adminName: "",
  mobileNumber: "",
  instituteType: 1,
  eiinNo: "",
};

/**
 * Platform-operator only (see `AccessService.isPlatformOperator` javadoc on
 * the backend) — direct port of digicampusbd's platform-admin institute
 * management (`InstituteController`'s `/list`/`/registration`/`/active`/
 * `/inactive`). Every institute on the platform, not the caller's own; no
 * real digicampusbd menu entry for this either (reached through a
 * standalone registration page in the old app, not a delegable sidebar
 * item) — same fixed-route pattern as BkashConfigPage/InstituteBillPage.
 * "Jump to institute" is deliberately not part of this page — see
 * MIGRATION.md and `PlatformInstituteService` javadoc for why.
 */
export default function PlatformInstitutesPage() {
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    listPlatformInstitutes()
      .then(setInstitutes)
      .catch(() => setError("Failed to load institutes."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await createPlatformInstitute({ ...form, instituteType: Number(form.instituteType) });
      setSuccess("Institute created successfully.");
      setModalOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to create institute.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (institute) => {
    const activating = !institute.status;
    if (!window.confirm(`${activating ? "Activate" : "Deactivate"} "${institute.instituteName}"?`)) return;
    setError("");
    try {
      if (activating) {
        await activatePlatformInstitute(institute.instituteId);
      } else {
        await deactivatePlatformInstitute(institute.instituteId);
      }
      setSuccess(`Institute ${activating ? "activated" : "deactivated"}.`);
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to update institute status.");
    }
  };

  const columns = [
    { key: "instituteId", label: "ID" },
    { key: "instituteName", label: "Name" },
    { key: "instituteType", label: "Type" },
    { key: "adminName", label: "Admin" },
    { key: "contactNumber", label: "Contact" },
    { key: "academicYear", label: "Academic Year" },
    { key: "instituteStatus", label: "Status" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Institute Management</h1>
        <button type="button" className="btn-primary" onClick={() => setModalOpen(true)}>
          + Create Institute
        </button>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={institutes}
          actions={(institute) => (
            <button type="button" className={`btn-link ${institute.status ? "danger" : ""}`} onClick={() => handleToggleStatus(institute)}>
              {institute.status ? "Deactivate" : "Activate"}
            </button>
          )}
        />
      )}

      {modalOpen && (
        <Modal title="Create Institute" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleCreate} className="user-form">
            <label>Institute Name *</label>
            <input value={form.instituteName} onChange={(e) => setForm({ ...form, instituteName: e.target.value })} required />
            <label>Address *</label>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required />
            <label>Institute Email *</label>
            <input type="email" value={form.instituteEmail} onChange={(e) => setForm({ ...form, instituteEmail: e.target.value })} required />
            <label>Institute Contact Number *</label>
            <input value={form.instituteContactNumber} onChange={(e) => setForm({ ...form, instituteContactNumber: e.target.value })} required />
            <label>Institute Type *</label>
            <select value={form.instituteType} onChange={(e) => setForm({ ...form, instituteType: e.target.value })}>
              <option value={1}>College</option>
              <option value={2}>High School</option>
              <option value={3}>Primary School</option>
            </select>
            <label>Admin Name *</label>
            <input value={form.adminName} onChange={(e) => setForm({ ...form, adminName: e.target.value })} required />
            <label>Admin Mobile Number *</label>
            <input value={form.mobileNumber} onChange={(e) => setForm({ ...form, mobileNumber: e.target.value })} required />
            <p style={{ fontSize: "0.85em", color: "var(--muted, #666)" }}>
              This mobile number becomes the new institute's super-admin login username, with the same number as the
              initial password.
            </p>
            <label>EIIN No.</label>
            <input value={form.eiinNo} onChange={(e) => setForm({ ...form, eiinNo: e.target.value })} />
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Creating..." : "Create Institute"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </MainLayout>
  );
}
